import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Location, useUpdateLocationMutation } from '@/services/gql/graphql.generated';
import { FunctionComponent, useState } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminDeactivateTaskCardProps {
  location: Location;
}

const config = {
  active: {
    title: adminStrings.locationsSection.deactivateReactivate.deactivate,
    icon: IoClose,
    info: adminStrings.locationsSection.deactivateReactivate.deactivateInfo,
    buttonText: adminStrings.common.deactivate,
    confirmTitle: adminStrings.locationsSection.deactivateReactivate.confirmTitle,
    confirmBody: adminStrings.locationsSection.deactivateReactivate.confirmDeactivateBody,
    successMessage: adminStrings.locationsSection.deactivateReactivate.successDeactivate,
    errorMessage: adminStrings.locationsSection.deactivateReactivate.errorDeactivate
  },
  inactive: {
    title: adminStrings.locationsSection.deactivateReactivate.reactivate,
    icon: IoRefresh,
    info: adminStrings.locationsSection.deactivateReactivate.reactivateInfo,
    buttonText: adminStrings.common.reactivate,
    confirmTitle: adminStrings.locationsSection.deactivateReactivate.confirmReactivateTitle,
    confirmBody: adminStrings.locationsSection.deactivateReactivate.confirmReactivateBody,
    successMessage: adminStrings.locationsSection.deactivateReactivate.successReactivate,
    errorMessage: adminStrings.locationsSection.deactivateReactivate.errorReactivate
  }
} as const;

const AdminDeactivateReactivateLocationCard: FunctionComponent<AdminDeactivateTaskCardProps> = ({ location }) => {
  const { title, icon, info, buttonText, confirmTitle, confirmBody, successMessage, errorMessage } =
    config[location.active ? 'active' : 'inactive'];

  const [{ fetching }, update] = useUpdateLocationMutation();
  const navigate = useNavigate();

  const onSubmit = async () => {
    const result = await update({ location: { ...location, active: !location.active } });

    await handleMutationResult(result, {
      successMessage,
      errorMessage,
      onSuccess: () => navigate('/admin/locations')
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <TSTitleCard title={title(location.name)} icon={icon} className="w-full">
      <div className="flex flex-row items-center space-x-3">
        <Button onClick={() => setIsModalOpen(true)} loading={fetching} danger tint>
          {buttonText}
        </Button>
        <p>{info}</p>
      </div>

      <ConfirmDialog
        onConfirm={onSubmit}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={confirmTitle(location.name)}
        confirmLabel={buttonText}
        cancelLabel={adminStrings.common.cancel}
      >
        {confirmBody}
      </ConfirmDialog>
    </TSTitleCard>
  );
};

export default AdminDeactivateReactivateLocationCard;
